import { useEffect, type ReactNode } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import ErrorBoundary from '@baaligames/shared-ui/components/ErrorBoundary';
import { AdminAuthProvider, useAdminAuth } from '@baaligames/shared-ui/admin/lib/adminAuth';
import { AdminLangProvider } from '@baaligames/shared-ui/admin/components/AdminLangContext';
import AdminAppShell from '@baaligames/shared-ui/admin/components/AdminAppShellV2';
import RequireCan from '@baaligames/shared-ui/admin/components/RequireCan';
import InstallPrompt from '@baaligames/shared-ui/lib/install';
import { useI18n, useT } from '@baaligames/shared-ui/lib/i18n';
import { SUPPORTED_LANGS } from '@baaligames/shared-ui/lib/translations';
import type { Lang } from '@baaligames/shared-ui/lib/translations';
// Phase R4 Step 3 — global interceptor for the forced-GPS pop-up.
// Mounts above the entire shell when an agent has no precise GPS
// on file, blocking all interaction with the app until they
// complete one-tap geolocation.
import GeoForcedModal from './components/GeoForcedModal';
import { isGeoLocated } from '@baaligames/shared-ui/components/LocationCapture';
import EmergencyLockoutGate from './components/EmergencyLockoutGate';
import MarginWarningModal from './components/MarginWarningModal';
// 2026-05-18 — Identity rollout soft-gate. Opens when first_name /
// last_name / birthdate are missing on the authenticated row.
// Dismissable per-session; re-opens on next mount until filled.
import CompleteProfileModal from './components/CompleteProfileModal';

// Standalone pages (login, more, dashboard, detail/workflow pages).
import AgentLoginPage from './pages/AgentLogin';
// U4 (2026-07-09): the top-level /rewards hub was retired — offers +
// referrals folded into Profile as the Rewards & referrals card
// (ProfileRewardsPanel reuses BrowseOffersPanel + ReferralsPanel; the
// jargon-heavy ExposurePanel died with the hub). /rewards/* redirects.
import AdminDashboard from './pages/AdminHome';
import AdminUserDetail from './pages/AdminUserDetail';
import AdminCreatePlayer from './pages/AdminCreatePlayer';
// Phase 16.6 — agent's own view of a DIRECT downline sub-agent.
// Backend gates every operation through loadDirectChildOr404 so a
// 404 hits the wire for any non-direct-child or stranger access.
import AgentSubAgentDetail from './pages/AgentSubAgentDetail';

// Phase 6 (2026-05-11): the /me/* MePoolHub was retired. Its
// content (Dashboard / Earnings / Leaderboard / Activity) is now
// embedded inside the unified mega-dashboard at /. Legacy /me/*
// paths redirect to /. The three surviving panel files were moved
// in Phase 7 from panels/me/ to panels/dashboard/ since they're
// now dashboard accordion contents, not "My Pool" tabs.

// Phase 4: ProfileHub — the agent's PERSONAL account.
// U4 (2026-07-09): rebuilt hub-and-spoke — card-menu landing + spokes.
import ProfileHub from './pages/ProfileHub';
import ProfileLandingPanel from './panels/profile/ProfileLandingPanel';
import ProfileRewardsPanel from './panels/profile/ProfileRewardsPanel';
import AccountPanel from './panels/profile/AccountPanel';
import MyWalletPanel from './panels/profile/MyWalletPanel';
import MyDepositPanel from './panels/profile/MyDepositPanel';
// Phase R8 (2026-05-14): agent's own rate matrix, read-only.
import MyRatesPanel from './panels/profile/MyRatesPanel';

// Phase 3.5: People hub fully collapsed into two consolidated pages
// — PlayersHub at /people/players and AgentsHub at /people/agents.
// Each hub has two sub-tabs: a "*** requests" panel (QR + queues +
// manual recharge form) and a "***" roster panel. The /finance hub
// is dismantled — its surface area lives inside these two hubs now.
import PlayersHub from './pages/PlayersHub';
import PlayerRequestsPanel from './panels/people/PlayerRequestsPanel';
import PlayersListPanel from './panels/people/PlayersListPanel';
import AgentsHub from './pages/AgentsHub';
import AgentRequestsPanel from './panels/people/AgentRequestsPanel';
import SubagentsListPanel from './panels/people/SubagentsListPanel';
import AgentCricketPanel from './pages/AgentCricketPanel';
import AgentMatkaPanel from './pages/AgentMatkaPanel';
// U3 (2026-07-09): the unified Games tab — replaces the Cricket-only
// bottom-nav slot. Cricket's detail page lives under it at /games/cricket.
import GamesHub from './pages/GamesHub';

function AgentOnly({ children }: { children: ReactNode }) {
  const { admin, loading } = useAdminAuth();
  // The auth-loading placeholder is the very first screen the agent
  // can see on a cold start, so it leans on the cross-app common.loading
  // key (same "Loading…" used everywhere else).
  if (loading) return <AuthGateLoading />;
  if (!admin) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function AuthGateLoading() {
  const t = useT();
  return <div className="p-10 text-center text-neutral-400">{t('common.loading')}</div>;
}

/**
 * Phase R4 Step 3 — agent forced-GPS interceptor.
 *
 * Renders the entire shell + app, but if the logged-in agent has
 * no precise GPS on file (latitude OR longitude is null), it ALSO
 * renders the GeoForcedModal on top with z-9999. The modal is non-
 * dismissible (see GeoForcedModal.tsx for the full lockdown
 * inventory). Rendering BOTH means once the modal disappears
 * (after a successful PATCH + auth refresh) the user is already
 * on whatever page they originally requested — no extra navigation.
 *
 * Trigger condition uses `==` not `===` so both `null` and
 * `undefined` qualify (the API may surface either depending on
 * driver version + JSON serialiser quirks).
 *
 * Admin-role users skip the gate entirely — only the `agent` role
 * receives players from the locator, so only agents need to
 * complete this flow. Admin can still log into the agent app
 * via the same auth (mode='admin' path in adminAuth.controller)
 * without being forced into the geo capture.
 */
function ShellRoute({ element }: { element: JSX.Element }) {
  const { admin } = useAdminAuth();
  // Phase R6 (2026-05-13) — broadened trigger. The modal now fires
  // whenever ANY of the six location fields is missing, not just
  // lat/lng. The Hyper-Local Mesh Network cascade needs every
  // level populated to route coherently: L1 needs lat/lng, L2 area,
  // L3 district, L4 state, and pincode_id is the unique routing key
  // inside L2. Any single null leaves a hole.
  const a = admin as unknown as {
    role?: string;
    latitude?: number | string | null;
    longitude?: number | string | null;
    state_id?: number | null;
    district_id?: number | null;
    area_id?: number | null;
    pincode_id?: number | null;
  };
  // L-6: agents located by GPS OR full area triple OR pincode — same rule as save gate + modal.
  const needsForcedGeo = admin?.role === 'agent' && !isGeoLocated(a);
  // A1 — Margin Call tri-state. Server computes:
  //   marginStatus = 'safe' | 'warning' (-wallet ≥ 80% of deposit)
  //                | 'locked' (-wallet ≥ 100% of deposit)
  // Locked = full-page hard gate (z-9999, non-dismissible).
  // Warning = recurring dismissible centered modal (z-9000) with
  //           on-mount + 10-min timer + 60s post-dismiss grace.
  // The two are mutually exclusive (server enforces) — but if state
  // ever flips locked while warning is up, the gate's higher z wins.
  const status = (admin as any)?.marginStatus as 'safe' | 'warning' | 'locked' | undefined;
  const showLockout = admin?.role === 'agent' && status === 'locked';
  const showWarning = admin?.role === 'agent' && status === 'warning';
  return (
    <AgentOnly>
      <AdminAppShell>{element}</AdminAppShell>
      {needsForcedGeo && <GeoForcedModal />}
      {showLockout   && <EmergencyLockoutGate />}
      {showWarning   && <MarginWarningModal />}
      {/* 2026-05-18 — Soft gate. CompleteProfileModal self-hides when
          first_name + last_name (and birthdate for agents) are set.
          Mounts after the other gates so its z-[80] tucks below the
          forced-GPS lockdown (which is intentionally non-dismissable
          at z-9999). The Margin gate at z-9000 also outranks it. */}
      <CompleteProfileModal />
    </AgentOnly>
  );
}

// Phase 10 follow-up (2026-05-12): mirror the player-end LanguageSync.
//
// When an agent signs in on a new device, the i18n context starts
// at whatever localStorage said (or 'en' if untouched). The server
// row carries the agent's last picked language — we read it off
// `admin.language` and write it back into the client i18n state so
// the same account speaks the same language across browsers / phones.
//
// Local explicit picks always win: if the user has already touched
// the LanguageSwitcher on this device, we leave the local choice
// alone (it'll PATCH back to the server via the dropdown anyway).
function LanguageSync() {
  const { admin } = useAdminAuth();
  const { lang, setLang } = useI18n();
  useEffect(() => {
    if (!admin) return;
    const saved = (admin as any).language as Lang | undefined;
    if (!saved) return;
    if (!SUPPORTED_LANGS.some((l) => l.code === saved)) return;
    const hasLocal = (() => {
      try { return !!localStorage.getItem('lang'); } catch { return false; }
    })();
    if (!hasLocal && saved !== lang) setLang(saved);
  }, [admin, lang, setLang]);
  return null;
}

// Phase 13.0: <Navigate to="..."> doesn't substitute :param tokens.
// This wrapper does the substitution from useParams() before navigating.
function SubstNavigate({ to }: { to: string }) {
  const params = useParams();
  let target = to;
  for (const [k, v] of Object.entries(params)) {
    if (typeof v === 'string') {
      target = target.split(`:${k}`).join(v);
    }
  }
  return <Navigate to={target} replace />;
}


// Agent app — agent.baaligames.com.
//
// Post-Phase-7 hub structure (2026-05-11):
//   /              → Unified AgentDashboard (Phase 6 mega-dashboard:
//                    morning briefing cards + accordions for P&L
//                    deep-dive, leaderboard, activity, earnings,
//                    direct sub-agents, chain visibility).
//   /people/*      → PlayersHub (/people/players + sub-tabs) and
//                    AgentsHub (/people/agents + sub-tabs).
//   /profile/*     → ProfileHub (wallet / deposit / account).
//   /rewards/*     → RewardsHub (browse / exposure).
//   /more, /login  → standalone.
//
// Legacy paths kept as REDIRECTS so external deep links never 404:
//   /users, /users/:id, /users/new, /users/:id/recharge → /people/players/*
//   /agents                                             → /people/agents
//   /finance/*, /money/*, /payment-settings             → consolidated hub paths
//   /me, /me/dashboard, /me/earnings, /me/leaderboard,  → /
//   /me/activity
//   /me/wallet, /me/deposit                             → /profile/*
//   /me/offer-exposure                                  → /rewards/exposure
export default function App() {
  return (
    <AdminAuthProvider>
      <AdminLangProvider>
        {/* Mirror /admin/auth/me's `language` field into the client i18n
            context on cold start / login. Renders nothing; just runs
            the useEffect that reconciles server → client language. */}
        <LanguageSync />
        <ErrorBoundary><Routes>
          <Route path="/login" element={<AgentLoginPage />} />

          {/* Dashboard */}
          <Route path="/" element={<ShellRoute element={<AdminDashboard />} />} />

          {/* ─── U3 (2026-07-09): GAMES HUB ─────────────────────────
              The unified Games tab: all-games hero + Cricket (main game)
              card + casino/small-games rows, registry-driven. Cricket's
              detailed earnings page moved under it at /games/cricket;
              the old /cricket URL redirects so muscle memory and saved
              links keep working. */}
          <Route path="/games"         element={<ShellRoute element={<GamesHub />} />} />
          <Route path="/games/cricket" element={<ShellRoute element={<AgentCricketPanel />} />} />
          <Route path="/games/matka_king" element={<ShellRoute element={<AgentMatkaPanel />} />} />
          <Route path="/cricket"       element={<Navigate to="/games/cricket" replace />} />

          {/* ─── Phase 3.5: PLAYERS HUB (consolidated) ──────────────
              /people/players hosts two sub-tabs: "Player requests"
              (QR module + manual recharge form + recharge/withdrawal
              queues) and "Players" (the user list). The deprecated
              /finance hub for player flows is replaced entirely by
              this hub; /finance/agents stays standalone until the
              Agents-side consolidation phase. */}
          <Route path="/people/players" element={<ShellRoute element={<RequireCan permKey="users" level="view"><PlayersHub /></RequireCan>} />}>
            <Route index             element={<Navigate to="requests" replace />} />
            <Route path="requests"   element={<PlayerRequestsPanel />} />
            <Route path="list"       element={<PlayersListPanel />} />
          </Route>
          {/* Detail + workflow pages (siblings, not tabs — full pages
              without the Players-hub sub-tab strip) */}
          <Route path="/people/players/:id"          element={<ShellRoute element={<RequireCan permKey="users" level="view"><AdminUserDetail /></RequireCan>} />} />
          <Route path="/people/players/new"          element={<ShellRoute element={<RequireCan permKey="users_create" level="edit"><AdminCreatePlayer /></RequireCan>} />} />

          {/* ─── Phase 3.5: AGENTS HUB (consolidated) ───────────────
              /people/agents hosts two sub-tabs: "Agent requests"
              (QR module + manual sub-agent recharge + agent-recharge
              / agent-withdrawal queues) and "Agents" (the sub-agent
              roster). Mirrors PlayersHub end-to-end. */}
          <Route path="/people/agents" element={<ShellRoute element={<RequireCan permKey="create_subagent" level="view" maxLevel={2}><AgentsHub /></RequireCan>} />}>
            <Route index             element={<Navigate to="requests" replace />} />
            <Route path="requests"   element={<AgentRequestsPanel />} />
            <Route path="list"       element={<SubagentsListPanel />} />
          </Route>
          {/* Sub-agent detail page — sibling, no hub sub-tabs. maxLevel=2 keeps
              leaf Agents (L3) out of the sub-agent pages entirely. */}
          <Route path="/people/agents/:id" element={<ShellRoute element={<RequireCan permKey="create_subagent" level="view" maxLevel={2}><AgentSubAgentDetail /></RequireCan>} />} />

          {/* /people root → land on the consolidated Players hub */}
          <Route path="/people" element={<Navigate to="/people/players" replace />} />

          {/* Legacy redirects → new hub paths */}
          <Route path="/users"               element={<Navigate to="/people/players" replace />} />
          <Route path="/users/:id"           element={<SubstNavigate to="/people/players/:id" />} />
          <Route path="/users/new"           element={<Navigate to="/people/players/new" replace />} />
          <Route path="/users/:id/recharge"  element={<SubstNavigate to="/people/players/requests?preselect=:id" />} />
          <Route path="/agents"              element={<Navigate to="/people/agents" replace />} />

          {/* Legacy /finance/* + /money/* + /payment-settings removed 2026-08-08
              (UX Phase 8 router hygiene): dismantled in Phase 3.5, ZERO hard-loads
              in 7 days of nginx logs, no in-app refs. The QR module + recharge/
              withdrawal queues live on /people/players/requests (players) and
              /people/agents/requests (sub-agents). Stale bookmarks fall through
              to the "*" catch-all → dashboard. */}

          {/* ─── Phase 12.2: MY POOL HUB ────────────────────────────── */}
          {/* Phase 6 (2026-05-11): /me/* fully consolidated into the
              unified dashboard at /. Every legacy path redirects
              there so deep links never 404. */}
          <Route path="/me"             element={<Navigate to="/" replace />} />
          <Route path="/me/dashboard"   element={<Navigate to="/" replace />} />
          <Route path="/me/earnings"    element={<Navigate to="/" replace />} />
          <Route path="/me/leaderboard" element={<Navigate to="/" replace />} />
          <Route path="/me/activity"    element={<Navigate to="/" replace />} />
          {/* Phase 4: /me/wallet and /me/deposit had already migrated
              to /profile/* — keep those redirects. */}
          <Route path="/me/wallet"      element={<Navigate to="/profile/wallet"  replace />} />
          <Route path="/me/deposit"     element={<Navigate to="/profile/deposit" replace />} />

          {/* ─── U4 (2026-07-09): PROFILE — hub-and-spoke, no tabs ──
              /profile lands on a plain card MENU; each card opens its
              own full page with a back bar. The security deposit lives
              ONLY here now (the wallet panel's duplicate deposit
              sub-tab is gone), and the old top-level /rewards hub is
              folded in as the Rewards & referrals card. */}
          <Route path="/profile" element={<ShellRoute element={<ProfileHub />} />}>
            <Route index           element={<ProfileLandingPanel />} />
            <Route path="wallet"   element={<MyWalletPanel />} />
            <Route path="deposit"  element={<MyDepositPanel />} />
            <Route path="rates"    element={<MyRatesPanel />} />
            <Route path="rewards"  element={<ProfileRewardsPanel />} />
            <Route path="account"  element={<AccountPanel />} />
          </Route>

          {/* Legacy redirects — the retired /rewards hub + /more all
              resolve to the Profile rewards card so every old bookmark
              and muscle-memory URL keeps working. */}
          <Route path="/more"              element={<Navigate to="/profile/rewards" replace />} />
          <Route path="/rewards"           element={<Navigate to="/profile/rewards" replace />} />
          <Route path="/rewards/*"         element={<Navigate to="/profile/rewards" replace />} />
          <Route path="/me/offer-exposure" element={<Navigate to="/profile/rewards" replace />} />

          {/* Catch-all → home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes></ErrorBoundary>
        <InstallPrompt variant="agent" />
      </AdminLangProvider>
    </AdminAuthProvider>
  );
}
